import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { PRODUCTS } from "../context/CartContext";
import "./Hero.css";

import jarHara from "../assets/jar-hara.webp";
import jarTurmeric from "../assets/jar-turmeric.webp";
import jarHoney from "../assets/jar-honey.webp";

const JAR_IMG = {
  "hara-namak": jarHara,
  turmeric: jarTurmeric,
  honey: jarHoney,
  "hemp-salt": jarHara,
  "flax-salt": jarHara,
};

const HERO = {
  "hara-namak": { word: "Hara Namak", headline: "The green salt of Pahadi kitchens", desc: "Pisi loon with green chilies and mountain herbs. No preservatives, no artificial colour — just the taste every Pahadi grew up with." },
  turmeric:     { word: "Turmeric",   headline: "Golden turmeric from mountain farms", desc: "Wild turmeric, grown in high-altitude soil. No polish, no shortcuts." },
  honey:        { word: "Honey",      headline: "Raw honey from wild Himalayan beehives", desc: "Harvested above 6,000 ft, unfiltered and unheated. Every jar tastes of the forest it came from." },
  "hemp-salt":  { word: "Hemp Salt",  headline: "Cold-pressed hemp with mineral salt", desc: "Hemp seeds blended with Himalayan mineral salt — the protein-rich Pahadi table companion." },
  "flax-salt":  { word: "Flax Salt",  headline: "Roasted flax with mountain minerals", desc: "Roasted flax seeds ground with mineral-rich salt. Nutty, earthy, and made for dal-chawal." },
};

const ORDER = ["hara-namak", "turmeric", "honey", "hemp-salt", "flax-salt"];
const AUTO_MS = 3000;

export default function Hero() {
  const [current, setCurrent] = useState("hara-namak");
  const [phase, setPhase] = useState("idle");
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef(null);
  const stageRef = useRef(null);
  const tiltRef = useRef(null);
  const barRefs = useRef({});
  const reduced = typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const product = PRODUCTS[current] || Object.values(PRODUCTS)[0];
  const hero = HERO[current] || { word: product.name, headline: product.name, desc: product.description };

  const runBar = useCallback((key) => {
    Object.values(barRefs.current).forEach((b) => {
      if (!b) return;
      b.classList.remove("run");
      b.style.width = "0%";
    });
    const bar = barRefs.current[key];
    if (bar) {
      void bar.offsetWidth;
      bar.classList.add("run");
    }
  }, []);

  const switchTo = useCallback((key) => {
    if (key === current) return;
    if (reduced) { setCurrent(key); return; }
    setPhase("exit");
    setTimeout(() => {
      setCurrent(key);
      setPhase("enter");
      requestAnimationFrame(() => requestAnimationFrame(() => setPhase("idle")));
    }, 390);
  }, [current, reduced]);

  useEffect(() => {
    runBar(current);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      switchTo(ORDER[(ORDER.indexOf(current) + 1) % ORDER.length]);
    }, AUTO_MS);
    return () => clearTimeout(timerRef.current);
  }, [current, runBar, switchTo]);

  useEffect(() => { setLoaded(true); }, []);

  useEffect(() => {
    if (reduced) return;
    const stage = stageRef.current;
    if (!stage) return;
    const move = (e) => {
      const r = stage.getBoundingClientRect();
      const dx = (e.clientX - r.left) / r.width - 0.5;
      const dy = (e.clientY - r.top) / r.height - 0.5;
      if (tiltRef.current)
        tiltRef.current.style.transform = `rotateY(${dx * 14}deg) rotateX(${-dy * 10}deg)`;
    };
    const leave = () => {
      if (tiltRef.current) tiltRef.current.style.transform = "rotateY(0) rotateX(0)";
    };
    stage.addEventListener("mousemove", move);
    stage.addEventListener("mouseleave", leave);
    return () => { stage.removeEventListener("mousemove", move); stage.removeEventListener("mouseleave", leave); };
  }, [reduced]);

  return (
    <section className={`pj-hero theme-${current} ${loaded ? "loaded" : ""}`} data-product={current}>
      <div className={`giant-word ${phase !== "idle" ? "slide" : ""}`}>{hero.word}</div>

      <div className="hero-copy">
        
        <h1 key={`h-${current}`} className="reveal">{hero.headline}</h1>
        <p key={`d-${current}`} className="desc reveal">{hero.desc}</p>
        <div className="cta-row">
          <Link to="/products" className="cta">Shop now</Link>
          <div className="price">
            <span>₹{product.price}</span>
            <span className="price-sub">Full jar {product.weight}g</span>
          </div>
        </div>
      </div>

      <div className="hero-stage" ref={stageRef}>
        <div className="float-el f1" /><div className="float-el f2" />
        <div className="float-el f3" /><div className="float-el f4" />
        <div className="jar-float jar-drop">
          <div className={`jar-slide ${phase}`}>
            <div className="jar-tilt" ref={tiltRef}>
              <img className="jar-photo" src={JAR_IMG[current]} alt={product.name} />
              <div className="shine" />
            </div>
          </div>
        </div>
      </div>

      <div className="hero-switcher" role="tablist" aria-label="Choose product">
        {ORDER.map((key) => {
          const p = PRODUCTS[key];
          if (!p) return null;
          return (
            <button key={key}
              className={`switch-btn ${key === current ? "active" : ""}`}
              onClick={() => switchTo(key)} role="tab" aria-selected={key === current}>
              <span className={`swatch swatch-${key}`} />
              <span className="sw-text">
                <span className="sw-name">{p.name}</span>
                <span className="sw-sub">{p.weight}g · ₹{p.price}</span>
                <span className="progress"><span className="bar" ref={(el) => (barRefs.current[key] = el)} /></span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="marquee">
        <div className="marquee-track">
          {Array(2).fill("Pure by Nature ✦ Quality Tested ✦ Proudly Pahadi ✦ No Preservatives ✦ Small Batch ✦ Customer Obsessed ✦ International Shipping ✦ First Purchase at ₹149 ✦ ").join("")}
        </div>
      </div>
    </section>
  );
}